import * as THREE from "three";
import { WORLD_GEN, outageLight, type Outage, type SunDef } from "@dyson/shared";

const gantryMaterial = new THREE.MeshBasicMaterial({ color: 0x2a2a30 });

let glowTexture: THREE.CanvasTexture | undefined;
function glow(): THREE.CanvasTexture {
  if (glowTexture) return glowTexture;
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.3, "rgba(255,255,255,0.35)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  glowTexture = new THREE.CanvasTexture(canvas);
  return glowTexture;
}

/**
 * A fission-fed artificial sun seen side-on: a glowing core, a wide additive
 * glow behind it, and a dark reactor gantry ring with spokes.
 */
export function buildSun(sun: SunDef): THREE.Group {
  const group = new THREE.Group();
  group.name = sun.id;
  group.position.set(sun.position.x, sun.position.y, 0);

  const halo = new THREE.Mesh(
    new THREE.PlaneGeometry(sun.radius * 7, sun.radius * 7),
    new THREE.MeshBasicMaterial({
      map: glow(),
      color: sun.color,
      transparent: true,
      opacity: 0.35 * sun.output,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: false,
    }),
  );
  halo.position.z = -3;
  group.add(halo);

  const core = new THREE.Mesh(
    new THREE.CircleGeometry(sun.radius, 64),
    // Several times brighter than white so the bloom pass gives it a real glow.
    new THREE.MeshBasicMaterial({ color: new THREE.Color(sun.color).multiplyScalar(4 * sun.output), fog: false }),
  );
  core.position.z = -1;
  group.add(core);

  const ring = new THREE.Mesh(new THREE.RingGeometry(sun.radius * 1.35, sun.radius * 1.45, 64), gantryMaterial);
  ring.position.z = -0.5;
  group.add(ring);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const spoke = new THREE.Mesh(new THREE.PlaneGeometry(sun.radius * 0.4, sun.radius * 0.05), gantryMaterial);
    spoke.position.set(Math.cos(a) * sun.radius * 1.2, Math.sin(a) * sun.radius * 1.2, -0.5);
    spoke.rotation.z = a;
    group.add(spoke);
  }
  return group;
}

/**
 * Ambient brightness at a world point: dim in the dark, warm near suns. A sun
 * while the sphere is shut down (`outage`) gives less as its dark walks in
 * (see outageLight), and inside its reach the ambient light goes too: a
 * system whose sun is out is properly night, lit only by lanterns and fire.
 */
export function lightAt(suns: readonly SunDef[], x: number, y: number, outage?: Outage | null): number {
  let l = 0.5;
  let night = 0;
  for (const s of suns) {
    const d = Math.hypot(x - s.position.x, y - s.position.y) - s.radius;
    const o = outage;
    const k = o ? outageLight(o, Math.max(0, d)) : 1;
    // Fading out as the life bands do (see WORLD_GEN.life), so a rock looks as lit as what grows on it.
    l += 0.6 * s.output * k * Math.max(0, 1 - d / WORLD_GEN.life.dark);
    if (o && d < WORLD_GEN.life.dark) night = Math.max(night, (1 - k) * Math.min(1, (WORLD_GEN.life.dark - d) / 200));
  }
  return Math.min(1.05, l - NIGHT_AMBIENT * night);
}

/** How much of the ambient light (0.5) goes in a system whose sun is out. */
const NIGHT_AMBIENT = 0.38;

/**
 * Draw a sun as bright as its outage leaves it (see Outage.output): its
 * glow shrinks and its core dims, down to a dull ember while it's out;
 * faltering, it flickers. `time` is the renderer's clock (s).
 */
export function setSunOutput(group: THREE.Object3D, sun: SunDef, output: number, time: number): void {
  const halo = group.children[0] as THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial> | undefined;
  const core = group.children[1] as THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial> | undefined;
  if (!halo || !core) return;
  halo.material.opacity = 0.35 * sun.output * output;
  // Out, an ember: a dull red core that breathes a little.
  const ember = (1 - output) * (0.35 + 0.08 * Math.sin(time * 1.3));
  const warm = new THREE.Color(sun.color).multiplyScalar(4 * sun.output * output);
  core.material.color.setRGB(warm.r + ember * 0.9, warm.g + ember * 0.18, warm.b + ember * 0.08);
}
